import { useMemo, type FC, type RefObject } from 'react'
import styled from 'styled-components'
import type { TypeCmsProductDetail } from '@/schema'
import { designGrid, getP18, mq, vw } from '@/styles'
import { Img, SliderFlickity } from './commons'
import { useTranslator } from '@/hooks'
import { getDynamicText } from '@/utils'

type Props = {
  data:{
    accessories:TypeCmsProductDetail['accessories']
    name: TypeCmsProductDetail['name']
  }
  ref: RefObject<HTMLElement>
}


const getSlides = (data:TypeCmsProductDetail['accessories']) => data.map((item, idx) => {
  if(!item) return <></>

  return (
    <DivSlide key={idx} className='slide'>
      <div className='media-wrapper'>
        {item.medium?.image ? <Img className='media' media={item.medium.image} sizes='(max-width: 768px) 80vw, 40vw' /> : <div className='media' />}
      </div>
      <hgroup>
        <h3>{item.name}</h3>
        <p>{item.model_reference}</p>
      </hgroup>
    </DivSlide>
  )
})

const headingId = 'accessories'
export const SectionAccessories:FC<Props> = ({ data, ref }) => {
  const { accessories, name } = data
  const Items = useMemo(() => getSlides(accessories), [accessories])
  const { t } = useTranslator()

  return (
    <Section ref={ref} aria-labelledby={headingId}>
      <h2 id={headingId}>
        {getDynamicText(t('project.product.recommended_accesories') as string, {
          product_name: name
        })} </h2>
      <SliderFlickity options={{ cellAlign: 'left', freeScroll:false, wrapAround: false, draggable: data.accessories.length > 4 ? true : false }}>
        {Items}
      </SliderFlickity>
    </Section>
  )
}

const Section = styled.section`
  ${designGrid({})}
  overflow-x: hidden;
  padding: 90px 0 100px;
  position: relative;

  ${mq.greaterThan('tablet')}{
    margin: 0;
    padding: 110px 0 100px;
  }

  h2 {
    ${getP18()}
    grid-area: 1 / 1 / span 1 / span 6;
    margin-bottom: ${vw(30, 'mobile')};
    text-align: left;

    ${mq.greaterThan('tablet')}{
      grid-area: 1 / 1 / span 1 / span 3;
      margin-bottom: ${vw(40, 'desktop')};
    }
  }

  > div {
    display: grid;
    grid-area: 2 / 1 / span 1 / span 6;
    grid-row-gap: ${vw(20, 'mobile')};
    grid-template-columns: 1fr 1fr;
    overflow-x: hidden;

    ${mq.greaterThan('tablet')}{
      grid-area: 2 / 1 / span 1 / span 12;
      grid-template-columns: repeat(4, 1fr);
      grid-row-gap: 0;
    }
  }


  `

const DivSlide = styled.div`
  margin-right: ${vw(23, 'mobile')};
  width: auto;

  ${mq.greaterThan('tablet')} {
    margin-right: ${vw(23, 'desktop')};
  }

  &:last-child {
    margin-right: 0;
  }

  div.media {
    min-width: ${vw(271, 'mobile')};

    ${mq.greaterThan('tablet')} {
      min-width: ${vw(320, 'desktop')};
    }
  }

  p, h3 {
    ${getP18()}
    text-align: center;
  }

`
